import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    CircularProgress,
    Alert,
    Grid
} from '@mui/material';
import { supabase } from '../supabase';
import { useSite } from "../SiteContext";
import { getSessionPhase, SESSION_PHASE, getCurrentSession, getNextSession, fetchSessions } from '../Utils/SessionUtils';
import { fetchVenueData } from '../Utils/VenueUtils';
import { useLocation } from 'react-router-dom';

import SessionsList from '../Components/welcome/SessionsList';
import InstructionsPanel from '../Components/welcome/InstructionsPanel';
import ParticipantsList from '../Components/welcome/ParticipantsList';
import SportVlaanderenHeader from '../Components/common/Headers';

/**
 * Fetches registered users for a specific session
 * @param {string} sessionId - ID of the session
 * @returns {Promise<Array>} - A promise that resolves to an array of users
 */
const fetchRegisteredUsers = async (sessionId) => {
    const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('session_id', sessionId);

    if (error) {
    console.error('Error fetching registered users:', error);
    return [];
    }

    return data || [];
};

/**
 * Welcome screen component that displays session information and registered users
 */
const Welcome = () => {
    const site = useSite();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [currentSession, setCurrentSession] = useState(null);
    const [registeredUsers, setRegisteredUsers] = useState([]);
    const [showInstructions] = useState(true);
    const [sessionPhase, setSessionPhase] = useState(null);
    const [venueId, setVenueId] = useState(null);
    const [venueData, setVenueData] = useState(null);
    const REFRESH_INTERVAL = 10000; // 10 seconds

    // Extract venue_id from URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const vId = params.get('venue_id') || 1; // Default to 1 if not provided
        setVenueId(vId);
    }, [location.search]);

    // Fetch venue data when venueId changes
    useEffect(() => {
        const loadVenueData = async () => {
            if (venueId) {
                const data = await fetchVenueData(venueId);
                if (data) {
                    setVenueData(data);
                }
            }
        };

        loadVenueData();
    }, [venueId]);

    // Add a new useEffect for periodic refresh of participants
    useEffect(() => {
        let intervalId;
        
        // Only set up polling if we have a current session
        if (currentSession) {
            const refreshParticipants = async () => {
                try {
                    const users = await fetchRegisteredUsers(currentSession.id);
                    // Only update if the number of participants changed or other data changed
                    if (JSON.stringify(users) !== JSON.stringify(registeredUsers)) {
                        setRegisteredUsers(users);
                    }
                } catch (err) {
                    console.error('Error refreshing participants:', err);
                }
            };
            
            // Initial fetch
            refreshParticipants();
            
            // Set up interval for periodic polling
            intervalId = setInterval(refreshParticipants, REFRESH_INTERVAL);
        }
        
        // Clean up interval on component unmount or when currentSession changes
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [currentSession, registeredUsers]);

    // Load current session and registered users
    useEffect(() => {
        const loadSessionData = async () => {
            try {
                if (venueId === null) return; // Wait until venueId is set
                
                setLoading(true);
                
                // Fetch available sessions
                const sessions = await fetchSessions(venueId);
                setSessions(sessions);
                console.log('Sessions for venue', venueId, ':', sessions);
                
                const activeSession = await getCurrentSession(sessions, venueId);
                
                if (activeSession) {
                    // Determine session phase
                    const phase = getSessionPhase(activeSession);
                    setSessionPhase(phase);
                    
                    // If in END phase, look for next session
                    if (sessionPhase === SESSION_PHASE.END) {
                        const nextSession = getNextSession(sessions, activeSession);
                        
                        // Use next session if available, otherwise fallback to current
                        const sessionToShow = nextSession || activeSession;
                        setCurrentSession(sessionToShow);
                        
                        // Fetch registered users for the selected session
                        const users = await fetchRegisteredUsers(sessionToShow.id);
                        setRegisteredUsers(users);
                    } else {
                        // Not in end phase, use current session
                        setCurrentSession(activeSession);
                        
                        // Fetch registered users for current session
                        const users = await fetchRegisteredUsers(activeSession.id);
                        setRegisteredUsers(users);
                    }
                }
            } catch (err) {
                console.error('Error loading session data:', err);
                setError('Er is een fout opgetreden bij het laden van de sessiegegevens.');
            } finally {
                setLoading(false);
            }
        };

        loadSessionData();
    }, [sessionPhase, venueId]); 

    if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        </Box>
    );
    }

    if (error) {
    return (
        <Container maxWidth="md">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
        </Container>
    );
    }

    if (!currentSession) {
        return (
          <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            <SportVlaanderenHeader />
            
            <Box sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: 'column',
              p: 4,
              justifyContent: 'center',
              maxWidth: '900px',
              mx: 'auto',
              width: '100%'
            }}>
              <Typography variant="h2" component="h1" gutterBottom sx={{ mb: 4, textAlign: 'center', fontSize: '3.5rem' }}>
                Programma
              </Typography>
              
              <SessionsList
                sessions={sessions}
                currentSession={null}
                sx={{ 
                  '& .MuiPaper-root': {
                    maxWidth: '100%'
                  }
                }}
              />
            </Box>
          </Box>
        );
      }

    return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <SportVlaanderenHeader venueData={venueData} />
            
        <Box sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            p: 3
        }}>
            <Typography variant="h2" component="h1" gutterBottom sx={{mb: 4, textAlign: 'center', fontSize: '3.5rem'}}>
                Welkom {currentSession.name}!
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>    
                    {showInstructions && (
                        <InstructionsPanel 
                            currentSession={currentSession}
                            venueId={venueId}
                            sx={{ flexGrow: 1 }}
                         />
                    )}
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <SessionsList
                            sessions={sessions}
                            currentSession={currentSession}
                        />

                        <ParticipantsList
                            participants={registeredUsers}
                            sx={{ flexGrow: 1, mt: 3 }}
                        />
                    </Box>  
                </Grid>
                
            </Grid>
        </Box>
    </Box>
    );
};

export default Welcome;